import React, { FC, ReactNode } from 'react';
import { Redirect, useRouteMatch } from 'react-router';
import { makeStyles } from '@material-ui/core/styles';
import classNames from 'classnames';

import { Layouts } from 'components';
import { useCurrentUser } from 'hooks';
import { isAdminUser } from 'helpers';
import * as paths from 'paths';

import AdminNav from './components/AdminNav';
import * as Containers from './containers';

const useStyles = makeStyles(theme => ({
  fullWidth: {
    margin: `0 auto ${theme.spacing(4)}px`,
    [theme.breakpoints.up('lg')]: {
      paddingRight: theme.spacing(3),
    },
  },
  maxWidth: {
    maxWidth: theme.breakpoints.values.md,
    [theme.breakpoints.up('lg')]: {
      paddingRight: 0,
    },
  },
}));

const AdminController: FC = () => {
  const match = useRouteMatch();
  const classes = useStyles();
  const currentUser = useCurrentUser();
  const isUserAdmin = isAdminUser(currentUser);

  if (!isUserAdmin) {
    return <Redirect to={paths.index} />;
  }

  const FullWidth = ({ children }: { children: ReactNode }) => (
    <div className={classes.fullWidth}>{children}</div>
  );
  const MaxWidth = ({ children }: { children: ReactNode }) => (
    <div className={classNames(classes.fullWidth, classes.maxWidth)}>{children}</div>
  );

  let main = null;

  switch (match.path) {
    case paths.adminAnalytics:
      main = <Redirect to={paths.adminAnalyticsOverview} />;
      break;
    case paths.adminAnalyticsGroups:
      main = (
        <FullWidth>
          <Containers.AnalyticsGroups />
        </FullWidth>
      );
      break;
    case paths.adminAnalyticsLeaderboards:
      main = (
        <FullWidth>
          <Containers.AnalyticsLeaderboards />
        </FullWidth>
      );
      break;
    case paths.adminAnalyticsOverview:
      main = (
        <FullWidth>
          <Containers.AnalyticsOverview />
        </FullWidth>
      );
      break;
    case paths.adminAnalyticsPosts:
      main = (
        <FullWidth>
          <Containers.AnalyticsPosts />
        </FullWidth>
      );
      break;
    case paths.adminAnalyticsShares:
      main = (
        <FullWidth>
          <Containers.AnalyticsShares />
        </FullWidth>
      );
      break;
    case paths.adminAnalyticsUsers:
      main = (
        <FullWidth>
          <Containers.AnalyticsUsers />
        </FullWidth>
      );
      break;
    case paths.adminPeople:
      main = (
        <FullWidth>
          <Containers.People />
        </FullWidth>
      );
      break;
    case paths.adminPeopleInvite:
      main = (
        <MaxWidth>
          <Containers.PeopleInvite />
        </MaxWidth>
      );
      break;
    case paths.adminGroups:
      main = (
        <FullWidth>
          <Containers.Groups />
        </FullWidth>
      );
      break;
    case paths.adminInvitations:
      main = (
        <FullWidth>
          <Containers.Invitations />
        </FullWidth>
      );
      break;
    case paths.adminMessages:
      main = (
        <FullWidth>
          <Containers.Messages />
        </FullWidth>
      );
      break;
    case paths.adminLeaderboards:
      main = (
        <FullWidth>
          <Containers.Leaderboards />
        </FullWidth>
      );
      break;
    case paths.adminReports:
      main = (
        <FullWidth>
          <Containers.Reports />
        </FullWidth>
      );
      break;
    case paths.adminContentEmails:
      main = (
        <FullWidth>
          <Containers.Emails />
        </FullWidth>
      );
      break;
    case paths.adminContentEmailEdit:
      main = (
        <MaxWidth>
          <Containers.EmailEdit />
        </MaxWidth>
      );
      break;
    case paths.adminContentEmailCreate:
      main = (
        <MaxWidth>
          <Containers.EmailCreate />
        </MaxWidth>
      );
      break;
    case paths.adminSettingsDepartments:
    case paths.adminSettingsLocations:
    case paths.adminSettingsDisclosures:
    case paths.adminSettingsPolicies:
      main = (
        <MaxWidth>
          <Containers.Account />
        </MaxWidth>
      );
      break;
    case paths.adminSettingsCompanyOnboarding:
    case paths.adminSettingsCustomOnboarding:
      main = (
        <MaxWidth>
          <Containers.Onboarding />
        </MaxWidth>
      );
      break;
    case paths.adminSettingsUserSegments:
      main = (
        <MaxWidth>
          <Containers.Segments />
        </MaxWidth>
      );
      break;
    case paths.adminSettingsShareTracking:
      main = (
        <MaxWidth>
          <Containers.Parameters />
        </MaxWidth>
      );
      break;
    case paths.adminSettingsSharing:
    case paths.adminSettingsStickers:
      main = (
        <MaxWidth>
          <Containers.Sharing />
        </MaxWidth>
      );
      break;
    default:
      throw new Error('Unhandled Admin Route');
  }

  return <Layouts.App2ColumnAdmin desktopSplit="2/10" sidebar={<AdminNav />} main={main} />;
};

export default AdminController;
